import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { sendMessage as sendMessageUtil, subscribeToMessages } from "@/utils/supabase";

interface Conversation {
  id: string;
  name: string;
  avatar: string;
  status: "online" | "offline";
  lastMessage: string;
  time: string;
  unread: number;
  userId: string;
}

interface Message {
  id: string | number;
  senderId: string;
  text: string;
  timestamp: string;
  isRead: boolean;
  attachment?: string;
  attachmentName?: string;
}

export const useMessages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(conversationId || null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());

  const presenceChannelRef = useRef<any>(null);
  const channelRef = useRef<any>(null);
  const typingTimeoutRef = useRef<any>(null);
  const globalPresenceChannelRef = useRef<any>(null);

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();

    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + 
             date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' }) + ' ' + 
           date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Simple notification creation - no complex session checking
  const createMessageNotification = async (senderId: string, conversationId: string) => {
    try {
      // Get conversation participants
      const { data: conv } = await supabase
        .from('conversations')
        .select('mentor_id, mentee_id')
        .eq('id', conversationId)
        .single();

      if (!conv) return;

      // Determine receiver
      const receiverId = conv.mentor_id === senderId ? conv.mentee_id : conv.mentor_id;

      // Get sender name
      const { data: senderProfile } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', senderId)
        .single();

      const senderName = senderProfile 
        ? `${senderProfile.first_name || ''} ${senderProfile.last_name || ''}`.trim() || 'Someone'
        : 'Someone';

      // Direct insert - simple approach
      const { error } = await supabase
        .from('notifications')
        .insert({
          receiver_id: receiverId,
          type: 'new_message',
          content: `${senderName} sent you a new message.`,
          link: `/messages/${conversationId}`
        });

      if (error) {
        console.error("❌ Notification insert failed:", error);
      } else {
        console.log("✅ Notification created successfully");
      }
    } catch (error) {
      console.error("❌ Error creating notification:", error);
    }
  };

  // Set up global presence channel for online status tracking
  useEffect(() => {
    if (!user?.id) return;

    console.log("Setting up global presence channel for online status");

    const globalPresenceChannel = supabase.channel('global-presence', {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    globalPresenceChannel
      .on('presence', { event: 'sync' }, () => {
        const state = globalPresenceChannel.presenceState();
        console.log("Global presence sync:", state);
        
        const onlineUserIds = new Set<string>();
        Object.values(state).forEach((users: any) => {
          users.forEach((userData: any) => {
            if (userData.online && userData.userId) {
              onlineUserIds.add(userData.userId);
            }
          });
        });
        
        setOnlineUsers(onlineUserIds);
        
        // Update conversations with online status
        setConversations(prevConversations =>
          prevConversations.map(conv => ({
            ...conv,
            status: onlineUserIds.has(conv.userId) ? "online" : "offline"
          }))
        );
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('User came online:', key, newPresences);
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('User went offline:', key, leftPresences);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          console.log("Global presence channel subscribed successfully");
          await globalPresenceChannel.track({
            userId: user.id,
            firstName: user.firstName || "",
            online: true,
            last_seen: new Date().toISOString(),
          });
        }
      });

    globalPresenceChannelRef.current = globalPresenceChannel;

    return () => {
      if (globalPresenceChannelRef.current) {
        console.log("Cleaning up global presence channel");
        supabase.removeChannel(globalPresenceChannelRef.current);
      }
    };
  }, [user?.id, user?.firstName]);

  // Fetch messages for selected conversation
  const fetchMessages = async (conversationId: string) => {
    if (!conversationId) return;
    
    setIsLoadingMessages(true);
    console.log("Fetching messages for conversation:", conversationId);

    try {
      const { data: messagesData, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error("Error fetching messages:", error);
        toast.error("Failed to load messages");
        return;
      }

      const formattedMessages: Message[] = messagesData.map(msg => ({
        id: msg.id,
        senderId: msg.sender_id,
        text: msg.content,
        timestamp: formatTimestamp(msg.created_at),
        isRead: msg.is_read,
        attachment: msg.attachment_url,
        attachmentName: msg.attachment_name
      }));

      console.log("Fetched messages:", formattedMessages);
      setMessages(formattedMessages);
    } catch (error) {
      console.error("Exception fetching messages:", error);
      toast.error("An error occurred while loading messages");
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Subscribe to real-time messages - FIXED
  useEffect(() => {
    if (!selectedConversation) return;

    console.log("🔄 Setting up message subscription for:", selectedConversation);

    const channel = subscribeToMessages(selectedConversation, async (payload) => {
      console.log("📨 Real-time message received:", payload);
      const newMsg = payload.new;
      
      const formattedMessage: Message = {
        id: newMsg.id,
        senderId: newMsg.sender_id,
        text: newMsg.content,
        timestamp: formatTimestamp(newMsg.created_at),
        isRead: newMsg.is_read,
        attachment: newMsg.attachment_url,
        attachmentName: newMsg.attachment_name
      };

      // Create notification ONLY for messages from other users
      if (newMsg.sender_id !== user?.id) {
        console.log("🔔 Creating notification for incoming message");
        await createMessageNotification(newMsg.sender_id, selectedConversation);
      }

      // Update messages list
      setMessages(prevMessages => {
        const exists = prevMessages.some(msg => msg.id === formattedMessage.id);
        if (exists) return prevMessages;
        return [...prevMessages, formattedMessage];
      });
    });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        console.log("🧹 Cleaning up message subscription");
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [selectedConversation, user?.id]);

  // Set up presence channel for typing indicators
  useEffect(() => {
    if (!selectedConversation || !user?.id) return;

    console.log("Setting up presence channel for conversation:", selectedConversation);

    const presenceChannel = supabase.channel(`presence:${selectedConversation}`, {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    presenceChannel
      .on('presence', { event: 'sync' }, () => {
        const state = presenceChannel.presenceState();
        console.log("Presence sync:", state);
        
        const typingUsersList: string[] = [];
        Object.values(state).forEach((users: any) => {
          users.forEach((userData: any) => {
            if (userData.typing && userData.userId !== user.id) {
              typingUsersList.push(userData.firstName || 'Someone');
            }
          });
        });
        
        setTypingUsers(typingUsersList);
        setIsTyping(typingUsersList.length > 0);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('User joined:', key, newPresences);
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('User left:', key, leftPresences);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          console.log("Presence channel subscribed successfully");
          await presenceChannel.track({
            userId: user.id,
            firstName: user.firstName || "",
            typing: false,
            online_at: new Date().toISOString(),
          });
        }
      });

    presenceChannelRef.current = presenceChannel;

    return () => {
      if (presenceChannelRef.current) {
        console.log("Cleaning up presence channel");
        supabase.removeChannel(presenceChannelRef.current);
      }
    };
  }, [selectedConversation, user?.id, user?.firstName]);

  const markMessagesAsRead = async (conversationId: string, userId: string) => {
    try {
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', userId)
        .eq('is_read', false);

      if (error) {
        console.error("Error marking messages as read:", error);
      }

      setConversations(prevConversations =>
        prevConversations.map(conv =>
          conv.id === conversationId ? { ...conv, unread: 0 } : conv
        )
      );
    } catch (error) {
      console.error("Exception in markMessagesAsRead:", error);
    }
  };

  // FIXED: Updated sendMessage to accept content parameter
  const sendMessage = async (content: string, attachmentUrl?: string, attachmentName?: string) => {
    if (!content.trim() && !attachmentUrl) {
      console.log("No content or attachment to send");
      return;
    }
    
    if (!selectedConversation || !user?.id) {
      console.log("Missing conversation or user ID");
      return;
    }

    try {
      const messageText = content.trim() || "";

      // Clear selected file if any
      setSelectedFile(null);

      // Stop typing indicator
      if (presenceChannelRef.current) {
        presenceChannelRef.current.track({
          userId: user?.id,
          firstName: user?.firstName || "",
          typing: false,
          online_at: new Date().toISOString(),
        });
      }

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      console.log("📤 Sending message:", { messageText, attachmentUrl, attachmentName });
      const result = await sendMessageUtil(
        selectedConversation, 
        user.id, 
        messageText,
        attachmentUrl, 
        attachmentName
      );

      if (!result.success) {
        console.error("❌ Error sending message:", result.error);
        toast.error("Failed to send message. Please try again.");
        return;
      }

      console.log("✅ Message sent successfully");
    } catch (error) {
      console.error("💥 Exception in sendMessage:", error);
      toast.error("An error occurred while sending your message");
    }
  };

  const handleSelectConversation = (id: string) => {
    console.log("Selecting conversation:", id);
    setSelectedConversation(id);
    navigate(`/messages/${id}`, { replace: true });

    // Fetch messages for the selected conversation
    fetchMessages(id);

    if (user?.id) {
      markMessagesAsRead(id, user.id);
    }
  };

  // Load messages when conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation);
    }
  }, [selectedConversation]);

  return {
    conversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    messages,
    setMessages,
    searchTerm,
    setSearchTerm,
    isLoadingConversations,
    setIsLoadingConversations,
    isLoadingMessages,
    setIsLoadingMessages,
    isTyping,
    setIsTyping,
    typingUsers,
    setTypingUsers,
    selectedFile,
    setSelectedFile,
    onlineUsers,
    presenceChannelRef,
    channelRef,
    typingTimeoutRef,
    formatTimestamp,
    markMessagesAsRead,
    sendMessage,
    handleSelectConversation
  };
};
